import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaUserCircle, FaCog, FaUsers } from 'react-icons/fa';
import { useAuth } from '@/context/AuthContext';
import DEFAULT_AVATAR from '@/assets/images/avatar/hd-man.png';
import { useTranslation } from 'react-i18next';
import { toCamelCase } from '@/utils/string-utils';

const ProfileWrapper = styled.div`
  padding: 1.5rem 1rem 1rem;
  border-bottom: 1px solid var(--color-gray-200);
  font-family: inherit;
`;

const CompactProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 0.75rem;
`;

const UserInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const UserName = styled.h6`
  font-size: 0.9rem;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  font-family: inherit;
`;

const UserRole = styled.p`
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
  font-family: inherit;
`;

const GroupsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const GroupBadge = styled.div`
  display: inline-block;
  background-color: #e3f2fd;
  color: #1976d2;
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 4px;
  margin-top: 2px;
`;

const ActionLinks = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-family: inherit;
  margin-top: 0.5rem;
`;

const ProfileLink = styled(Link)`
  color: #3b82f6;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-family: inherit;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    margin-right: 0.25rem;
  }
`;

const UserProfileSidebar = ({isCollapsed}: {isCollapsed?: boolean}) => {
  const { t } = useTranslation('commonNew');
  const { t: tGroups } = useTranslation('groups');
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  // Tạo tên hiển thị từ thông tin user
  const displayName = user.displayName || user.username;

  if(isCollapsed) {
    return (
      <div className='w-full py-6 flex justify-center text-center !pb-6 border-b border-gray-200'>
        <Avatar
          className='m-0'
          src={user?.avatar ? user.avatar : DEFAULT_AVATAR}
          alt={displayName}
        />
      </div>
      
    )
  }

  return (
    <ProfileWrapper>
      <CompactProfileHeader>
        <Avatar
          src={user?.avatar ? user.avatar : DEFAULT_AVATAR}
          alt={displayName}
        />
        <UserInfo>
          <UserName>{displayName}</UserName>
          <UserRole>{user.role?.name || 'Người dùng'}</UserRole>

          {/* Hiển thị danh sách nhóm */}
          {user.groups && user.groups.length > 0 && (
            <GroupsContainer>
              {user.groups.slice(0, 2).map((group, index) => (
                <GroupBadge key={index} title={group.description || ''}>
                  {tGroups(`${toCamelCase(`${group.code}`)}.name`, {defaultValue: group.name || group.code})}
                </GroupBadge>
              ))}
              {user.groups.length > 2 && (
                <GroupBadge title="Xem tất cả nhóm">
                  +{user.groups.length - 2}
                </GroupBadge>
              )}
            </GroupsContainer>
          )}
        </UserInfo>
      </CompactProfileHeader>

      <ActionLinks>
        <ProfileLink to="/profile">
          <FaUserCircle size={12} />
          <span>{t('userProfile', {defaultValue: 'Hồ sơ cá nhân'})}</span>
        </ProfileLink>
        <ProfileLink
          to="/settings/account"
          className="pointer-events-none !text-gray-300" // TODO REWORK
        >
          <FaCog size={12} />
          <span>{t('setting', {defaultValue: 'Cài đặt'})}</span>
        </ProfileLink>
      </ActionLinks>
    </ProfileWrapper>
  );
};

export default UserProfileSidebar;